import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Loader2,
  Plus,
  Trash2,
  Brain,
  X,
  Check,
  Search,
  RefreshCw,
  FileText,
  File,
  FileImage,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Tip } from "@/components/Tip";
import {
  fetchProjectKnowledge,
  fetchProjectFiles,
  ingestProjectKnowledge,
  reindexProjectKnowledge,
  deleteProjectKnowledge,
  searchProjectKnowledge,
  projectNotFoundMessage,
  isProjectNotFound,
  type KnowledgeDoc,
  type KnowledgeSearchHit,
  type ProjectFileItem,
} from "@/lib/api/projects";

interface Props {
  projectId: string;
  projectLabel: string;
}

/** MIMEs / extensions the backend can extract text from (TXT/MD/PDF). */
const SUPPORTED: { mime?: string; exts: string[]; label: string }[] = [
  { mime: "application/pdf", exts: [".pdf"], label: "PDF" },
  { mime: "text/markdown", exts: [".md", ".markdown"], label: "Markdown" },
  { mime: "text/plain", exts: [".txt"], label: "TXT" },
];

function fileIsSupported(f: ProjectFileItem): boolean {
  const name = (f.name || "").toLowerCase();
  const ext = name.includes(".") ? name.slice(name.lastIndexOf(".")) : "";
  const mime = (f.mime || "").toLowerCase();
  return SUPPORTED.some((s) => (s.mime && mime === s.mime) || s.exts.includes(ext));
}

function FileIcon({ f }: { f: ProjectFileItem }) {
  const name = (f.name || "").toLowerCase();
  if (name.endsWith(".pdf") || (f.mime || "").includes("pdf")) return <FileText size={15} />;
  if (f.mime?.includes("image") || /\.(png|jpe?g|webp|gif)$/.test(name)) return <FileImage size={15} />;
  return <File size={15} />;
}

const STATUS_LABEL: Record<string, string> = {
  pending: "Ожидает",
  ready: "Готово",
  failed: "Ошибка",
  archived: "Файл удалён",
};

const STATUS_COLOR: Record<string, string> = {
  pending: "bg-amber-500/15 text-amber-400",
  ready: "bg-emerald-500/15 text-emerald-400",
  failed: "bg-rose-500/15 text-rose-400",
  archived: "bg-slate-500/15 text-slate-400",
};

export function ProjectKnowledge({ projectId, projectLabel }: Props) {
  const [docs, setDocs] = useState<KnowledgeDoc[]>([]);
  const [files, setFiles] = useState<ProjectFileItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  // search
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [hits, setHits] = useState<KnowledgeSearchHit[] | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [kb, page] = await Promise.all([
        fetchProjectKnowledge(projectId),
        fetchProjectFiles(projectId),
      ]);
      setDocs(kb);
      setFiles(page.files);
    } catch (e) {
      setError(isProjectNotFound(e) ? projectNotFoundMessage() : "Не удалось загрузить базу знаний");
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  // Candidate files: those not already in the KB (by user_file_id).
  const inKbFileIds = useMemo(() => new Set(docs.map((d) => d.user_file_id)), [docs]);
  const candidates = useMemo(() => files.filter((f) => !inKbFileIds.has(f.id)), [files, inKbFileIds]);

  const add = async (fileId: string) => {
    setBusyId(fileId);
    setError(null);
    try {
      await ingestProjectKnowledge(projectId, fileId);
      setShowAdd(false);
      await load();
    } catch (e) {
      setError(
        isProjectNotFound(e)
          ? projectNotFoundMessage()
          : e instanceof Error && e.message
            ? e.message
            : "Не удалось добавить документ",
      );
    } finally {
      setBusyId(null);
    }
  };

  const reindex = async (id: string) => {
    setBusyId(id);
    setError(null);
    try {
      await reindexProjectKnowledge(projectId, id);
      await load();
    } catch (e) {
      setError(isProjectNotFound(e) ? projectNotFoundMessage() : "Не удалось переиндексировать");
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (id: string) => {
    setConfirmDelete(null);
    setBusyId(id);
    try {
      await deleteProjectKnowledge(projectId, id);
      await load();
    } catch (e) {
      setError(isProjectNotFound(e) ? projectNotFoundMessage() : "Не удалось удалить документ");
    } finally {
      setBusyId(null);
    }
  };

  const doSearch = async () => {
    const q = query.trim();
    if (!q || searching) return;
    setSearching(true);
    setSearchError(null);
    setHits(null);
    try {
      const res = await searchProjectKnowledge(projectId, q, 8);
      setHits(res.results);
    } catch (e) {
      setSearchError(isProjectNotFound(e) ? projectNotFoundMessage() : "Не удалось выполнить поиск");
    } finally {
      setSearching(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-medium text-primary">База знаний</h2>
          <p className="text-xs text-tertiary mt-0.5">
            Документы проекта (TXT, Markdown, PDF), которые модель находит и использует в
            контексте чатов «{projectLabel}».
          </p>
        </div>
        {!showAdd && (
          <button
            onClick={() => setShowAdd(true)}
            className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-lg text-sm font-medium bg-accent/15 text-accent hover:bg-accent/25 transition-colors"
          >
            <Plus size={15} />
            Добавить документ
          </button>
        )}
      </div>

      {/* Add picker (manual ingestion) */}
      {showAdd && (
        <div className="rounded-xl border border-hairline bg-surface p-4 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-secondary">
              Добавить файл проекта в базу знаний
            </span>
            <button onClick={() => setShowAdd(false)} className="text-tertiary hover:text-primary" aria-label="Закрыть">
              <X size={16} />
            </button>
          </div>
          {candidates.length === 0 ? (
            <p className="text-sm text-secondary">
              Нет доступных файлов. Загрузите TXT, Markdown или PDF во вкладку «Файлы», затем
              добавьте его сюда.
            </p>
          ) : (
            <ul className="space-y-1.5 max-h-64 overflow-y-auto">
              {candidates.map((f) => {
                const ok = fileIsSupported(f);
                return (
                  <li key={f.id} className="flex items-center gap-2">
                    <span className={cn("shrink-0", ok ? "text-secondary" : "text-tertiary/60")}>
                      <FileIcon f={f} />
                    </span>
                    <span className={cn("text-sm truncate flex-1", ok ? "text-primary" : "text-tertiary line-through")}>
                      {f.name}
                    </span>
                    <span className="text-[11px] text-tertiary shrink-0">
                      {(f.size / 1024).toFixed(0)} KB
                    </span>
                    <button
                      onClick={() => void add(f.id)}
                      disabled={!ok || busyId === f.id}
                      title={ok ? "Добавить в базу знаний" : "Формат не поддерживается"}
                      className={cn(
                        "shrink-0 h-7 px-2.5 rounded-md text-xs font-medium inline-flex items-center gap-1 transition-colors",
                        ok
                          ? "bg-accent/15 text-accent hover:bg-accent/25 disabled:opacity-50"
                          : "text-tertiary/50 cursor-not-allowed",
                      )}
                    >
                      {busyId === f.id ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
                      Добавить
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          <p className="text-[11px] text-tertiary">
            Поддерживаются: TXT, Markdown, PDF. Индексация выполняется вручную; файл остаётся во
            вкладке «Файлы».
          </p>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-400">
          {error}
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="py-12 flex items-center justify-center">
          <Loader2 size={20} className="animate-spin text-tertiary" />
        </div>
      ) : !error && docs.length === 0 ? (
        <div className="py-12 flex flex-col items-center gap-3 text-center">
          <Brain size={28} className="text-tertiary" />
          <p className="text-sm text-secondary">База знаний пока пуста.</p>
          <p className="text-xs text-tertiary max-w-xs">
            Добавьте файлы проекта — их содержимое станет доступно модели через полнотекстовый
            поиск по проекту.
          </p>
        </div>
      ) : (
        <ul className="space-y-2">
          {docs.map((d) => (
            <li key={d.id} className="rounded-xl border border-hairline bg-surface px-4 py-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={cn(
                      "inline-flex items-center rounded-md px-1.5 py-0.5 text-[11px] font-medium",
                      STATUS_COLOR[d.status] ?? "bg-slate-500/15 text-slate-400",
                    )}>
                      {STATUS_LABEL[d.status] ?? d.status}
                    </span>
                    <span className="text-sm font-medium text-primary truncate">{d.file_name}</span>
                    {d.chunk_count > 0 && (
                      <span className="text-[11px] text-tertiary">
                        {d.chunk_count} фрагм.
                      </span>
                    )}
                  </div>
                  {d.error && (
                    <p className="mt-1.5 text-xs text-rose-400 break-words">{d.error}</p>
                  )}
                  {d.indexed_at && (
                    <p className="mt-1 text-[11px] text-tertiary">
                      Индексация: {new Date(d.indexed_at).toLocaleString()}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {d.status === "ready" && (
                    <Tip label="Переиндексировать">
                      <button
                        onClick={() => void reindex(d.id)}
                        disabled={busyId === d.id}
                        className="w-7 h-7 rounded-md flex items-center justify-center text-secondary hover:text-primary hover:bg-hoverbg transition-colors disabled:opacity-50"
                        aria-label="Переиндексировать"
                      >
                        {busyId === d.id ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                      </button>
                    </Tip>
                  )}
                  <Tip label="Удалить из базы знаний">
                    <button
                      onClick={() => setConfirmDelete(d.id)}
                      className="w-7 h-7 rounded-md flex items-center justify-center text-secondary hover:text-rose-400 hover:bg-hoverbg transition-colors"
                      aria-label="Удалить из базы знаний"
                    >
                      <Trash2 size={14} />
                    </button>
                  </Tip>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Search (retrieval demo) */}
      {!loading && docs.some((d) => d.status === "ready") && (
        <div className="rounded-xl border border-hairline bg-surface p-4 space-y-3">
          <div className="flex items-center gap-2">
            <Search size={15} className="text-secondary" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && void doSearch()}
              placeholder="Поиск по базе знаний…"
              className="flex-1 h-9 px-3 rounded-lg bg-hoverbg border border-hairline text-sm text-primary focus:outline-none focus:border-accent"
            />
            <button
              onClick={() => void doSearch()}
              disabled={searching || !query.trim()}
              className="h-9 px-3.5 rounded-lg text-sm font-medium bg-accent/15 text-accent hover:bg-accent/25 transition-colors disabled:opacity-50"
            >
              {searching ? <Loader2 size={14} className="animate-spin" /> : "Найти"}
            </button>
          </div>
          {searchError && (
            <p className="text-xs text-rose-400">{searchError}</p>
          )}
          {hits !== null && (
            hits.length === 0 ? (
              <p className="text-sm text-secondary">Ничего не найдено по запросу «{query.trim()}».</p>
            ) : (
              <ul className="space-y-2 max-h-64 overflow-y-auto">
                {hits.map((h, i) => (
                  <li key={`${h.knowledge_id}-${h.seq}-${i}`} className="text-sm">
                    <div className="flex items-center gap-2 text-[11px] text-tertiary mb-1">
                      {h.file_name && <span className="text-secondary">[doc: {h.file_name}]</span>}
                      <span>фрагмент {h.seq}</span>
                    </div>
                    <p className="text-secondary whitespace-pre-wrap break-words">{h.content}</p>
                  </li>
                ))}
              </ul>
            )
          )}
        </div>
      )}

      {/* Delete confirm */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div role="dialog" className="w-full max-w-sm rounded-2xl border border-hairline bg-surface p-5 space-y-4">
            <div className="flex items-center gap-2">
              <Trash2 size={18} className="text-rose-400" />
              <h3 className="text-sm font-medium text-primary">Удалить документ из базы знаний?</h3>
            </div>
            <p className="text-sm text-secondary">
              Документ перестанет использоваться в контексте проекта. Сам файл во вкладке «Файлы»
              останется на месте.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setConfirmDelete(null)}
                className="h-9 px-3.5 rounded-lg text-sm text-secondary hover:text-primary hover:bg-hoverbg transition-colors"
              >
                Отмена
              </button>
              <button
                onClick={() => void remove(confirmDelete)}
                className="h-9 px-3.5 rounded-lg text-sm font-medium bg-rose-500/15 text-rose-400 hover:bg-rose-500/25 transition-colors"
              >
                Удалить
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
